/**
 * Het profiel van de ingelogde gebruiker: weergavenaam, e-mailadres en — als de
 * aanmelding er een meelevert — een profielfoto.
 *
 * De API kent de gebruiker alleen als `x-ms-client-principal`, en daar staat niet meer
 * in dan het UPN (`adm_martin@kdvs.nl`), het id en de rollen. Dat is prima om rechten
 * op te hangen, maar het is geen naam om onder de navigatie te zetten. De volledige
 * claims van de aanmelding staan wél op `/.auth/me` van de Static Web App: daar komen
 * `name`, `given_name`, `family_name` en `preferred_username` uit, en bij een
 * identiteitsprovider die het meestuurt ook `picture`.
 *
 * Entra ID levert standaard géén `picture`-claim. De foto komt daarom van de eigen API:
 * `/api/profielfoto` haalt hem met Microsoft Graph op (zie `api/src/functions/profiel.ts`
 * voor de drie app settings en de toestemming die daarvoor nodig zijn). Is die koppeling
 * er niet, of heeft deze gebruiker geen foto in Microsoft 365 staan, dan antwoordt dat
 * endpoint met 204 en blijft het initialenvlak staan — dezelfde vorm en maat, zodat het
 * scherm er niet anders van gaat staan als de foto er later bij komt.
 */

export interface Profiel {
  /** `Martin Slegers`, of het beste dat uit de claims te maken is. */
  weergavenaam: string;
  voornaam: string;
  achternaam: string;
  /** Het adres waarmee is aangemeld. */
  email: string;
  /** Alleen gevuld als de aanmelding een foto meelevert. */
  fotoUrl: string | null;
}

interface AuthClaim {
  typ: string;
  val: string;
}

interface AuthAntwoord {
  clientPrincipal: {
    userDetails?: string;
    userId?: string;
    userRoles?: string[];
    claims?: AuthClaim[];
  } | null;
}

/** De claimnamen komen in twee vormen voorbij: kort en als volledige URI. */
function claim(claims: AuthClaim[], ...namen: string[]): string {
  for (const naam of namen) {
    const gevonden = claims.find(
      (c) => c.typ === naam || c.typ.toLowerCase().endsWith(`/${naam.toLowerCase()}`),
    );
    if (gevonden?.val) return gevonden.val.trim();
  }
  return '';
}

/**
 * Een leesbare naam uit een aanmeldadres, als terugval.
 *
 * `adm_martin@kdvs.nl` → `Martin`, `jan.de.vries@kdvs.nl` → `Jan De Vries`. De
 * beheerprefixen die bij KDVS voor servicelogins worden gebruikt gaan eraf; wat
 * overblijft krijgt hoofdletters per woord. Het is een benadering — de echte naam komt
 * uit de claims — maar hij is altijd beter dan het hele adres.
 */
export function naamUitAdres(adres: string): string {
  const lokaal = adres.split('@')[0] ?? adres;
  const delen = lokaal
    .split(/[._-]+/)
    .filter(Boolean)
    .filter((deel) => !['adm', 'admin', 'svc', 'sa', 'test'].includes(deel.toLowerCase()));
  if (delen.length === 0) return adres;
  return delen.map((deel) => deel.charAt(0).toUpperCase() + deel.slice(1)).join(' ');
}

/**
 * De profielfoto bij de eigen API opvragen.
 *
 * 200 met een afbeelding erin is een foto; 204 betekent "geen foto" en alles daarbuiten
 * is een storing. In alle gevallen behalve de eerste blijft het `null` en toont de
 * zijbalk initialen. De blob wordt een object-URL, zodat het `<img>`-element er direct
 * mee overweg kan zonder de afbeelding nog een keer op te halen.
 */
async function haalFoto(): Promise<string | null> {
  try {
    const antwoord = await fetch('/api/profielfoto');
    if (antwoord.status !== 200) return null;
    const blob = await antwoord.blob();
    return blob.size > 0 ? URL.createObjectURL(blob) : null;
  } catch {
    return null;
  }
}

/** Voor- en achternaam uit een volledige naam; `Slegers, Martin` mag ook. */
function splits(volledig: string): { voornaam: string; achternaam: string } {
  const naam = volledig.includes(',')
    ? volledig
        .split(',')
        .map((d) => d.trim())
        .reverse()
        .join(' ')
    : volledig;
  const delen = naam.split(/\s+/).filter(Boolean);
  if (delen.length === 0) return { voornaam: '', achternaam: '' };
  return { voornaam: delen[0] ?? '', achternaam: delen.slice(1).join(' ') };
}

/**
 * Het profiel ophalen. Mislukt dat — lokaal draaien zonder aanmelding, of een SWA die
 * de claims niet meelevert — dan wordt het profiel opgebouwd uit de naam die de API al
 * kende.
 */
export async function haalProfiel(apiNaam: string | undefined): Promise<Profiel> {
  const terugval = async (): Promise<Profiel> => {
    const adres = apiNaam ?? '';
    const isAdres = adres.includes('@');
    const volledig = isAdres ? naamUitAdres(adres) : adres;
    // Zonder aanmelding — lokaal draaien — is er geen adres, alleen een naam. Dan blijft
    // het e-mailveld leeg in plaats van dat er een naam op de adresregel staat, en is er
    // ook geen foto op te halen.
    return {
      weergavenaam: volledig || adres,
      ...splits(volledig),
      email: isAdres ? adres : '',
      fotoUrl: isAdres ? await haalFoto() : null,
    };
  };

  try {
    const antwoord = await fetch('/.auth/me', { headers: { accept: 'application/json' } });
    if (!antwoord.ok) return await terugval();
    const gegevens = (await antwoord.json()) as AuthAntwoord;
    const principal = gegevens.clientPrincipal;
    if (!principal) return await terugval();

    const claims = principal.claims ?? [];
    const email =
      claim(claims, 'preferred_username', 'email', 'emailaddress', 'upn') ||
      principal.userDetails ||
      apiNaam ||
      '';
    const voornaamClaim = claim(claims, 'given_name', 'givenname');
    const achternaamClaim = claim(claims, 'family_name', 'surname');
    const volledig =
      [voornaamClaim, achternaamClaim].filter(Boolean).join(' ') ||
      claim(claims, 'name') ||
      (email.includes('@') ? naamUitAdres(email) : email);
    const gesplitst = splits(volledig);

    return {
      weergavenaam: volledig || email,
      voornaam: voornaamClaim || gesplitst.voornaam,
      achternaam: achternaamClaim || gesplitst.achternaam,
      email,
      // Levert de aanmelding zelf een foto, dan die; anders bij Graph langs.
      fotoUrl: claim(claims, 'picture') || (await haalFoto()),
    };
  } catch {
    return await terugval();
  }
}
